import React, { useRef, useCallback, useEffect } from 'react';
import _get from 'lodash/get';

import { logError } from 'utils';
import { useFetch, useIdle, useOnline, useStopwatch } from 'utils/hooks';

const REVALIDATION_INTERVAL = 10; // seconds
const REVALIDATION_ERROR_THRESHOLD = 3; // times
const IS_PROD = process.env.REACT_APP_MODE === 'PRODUCTION';
const CURRENCT_BUILD_ID = process.env.REACT_APP_BUILD_ID;

export default function NetlifySync({ isEnabled = true }) {
  const isUserIdle = useIdle();
  const isOnline = useOnline();
  const { seconds, isRunning, start, pause, reset } = useStopwatch();

  const netlifySyncFailureCount = useRef(0);
  const isWatching = isEnabled && isOnline && !isUserIdle && IS_PROD;

  const restart = useRef(() => {
    reset();
    start();
  }).current;

  const stop = useRef(() => {
    reset();
    pause();
  }).current;

  const { fetchData: getPublishedDeployment } = useFetch('ui.deploys', {
    loadOnMount: false,
    isPublicAPI: true,
    errorMessage: '',
    routeParams: { siteId: process.env.REACT_APP_SITE_ID },
    payload: { per_page: 1, 'latest-published': true },
    onTransform: data => _get(data, '[0]', {}),
    onSuccess: deploy => {
      netlifySyncFailureCount.current = 0;
      const publishedBuildId = _get(deploy, 'build_id', '');
      if (CURRENCT_BUILD_ID !== publishedBuildId) {
        window.location.reload();
      }
    },
    onError: () => netlifySyncFailureCount.current++,
    onFinally: () => {
      if (netlifySyncFailureCount.current >= REVALIDATION_ERROR_THRESHOLD) {
        logError(new Error('Aborting Netlify sync | Failed 3 times in a row'));
      } else {
        restart();
      }
    }
  });

  const validate = useCallback(() => {
    stop();

    if (netlifySyncFailureCount.current >= REVALIDATION_ERROR_THRESHOLD) return;

    getPublishedDeployment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isWatching) {
      validate();
    } else {
      stop();
    }
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWatching, validate]);

  useEffect(() => {
    if (isRunning && seconds >= REVALIDATION_INTERVAL) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, seconds]);

  useEffect(() => {
    if (isWatching) {
      const listener = () => {
        if (document.visibilityState === 'visible') {
          validate();
        } else {
          stop();
        }
      };

      window.addEventListener('visibilitychange', listener);
      return () => window.removeEventListener('visibilitychange', listener);
    }
  }, [isWatching, validate, stop]);

  return <></>;
}
